import { useEffect, useRef } from 'react';
import type { User } from 'firebase/auth';
import { recordLogin } from '@shared/services/activityDayService';
import { recordAttributionVisit } from '@shared/services/attributionService';
import { useDayChange } from './useDayChange';

/**
 * Keep returning-user measurement honest on native resume and midnight
 * rollover. iOS resumes the WebView without remounting, so
 * onAuthStateChanged does not fire again — AuthContext's recordLogin /
 * recordAttributionVisit never run, and a day-2 open with no tap writes
 * neither an activityDays doc nor a lastVisitAt bump. The north star and
 * the experiment-funnel "returned" / "secondSessions" rows then read as
 * zero for people who did come back.
 *
 * recordLogin is idempotent per local day. Attribution visits still belong
 * to AuthContext for the first settle of a JS session; this hook only
 * records a visit when the local date actually changes.
 */
export function usePresenceRecording(user: User | null): void {
    const localDate = useDayChange();
    const uid = user?.uid;
    const isAnonymous = !user || user.isAnonymous;
    const lastVisitDateRef = useRef<string | null>(null);

    // New uid: AuthContext records that session's first visit. Reset so we
    // don't double-count it here, then stamp login for the current day.
    useEffect(() => {
        lastVisitDateRef.current = null;
    }, [uid]);

    useEffect(() => {
        if (!uid) return;
        if (!isAnonymous) {
            recordLogin(uid).catch(() => {});
        }
        if (lastVisitDateRef.current === null) {
            lastVisitDateRef.current = localDate;
            return;
        }
        if (lastVisitDateRef.current !== localDate) {
            lastVisitDateRef.current = localDate;
            recordAttributionVisit(uid, isAnonymous);
        }
    }, [uid, isAnonymous, localDate]);
}
